import { uploadMedia } from "../../../../features/media/utils/mediaUploadUtils";
import type { DraftPost } from "../types";
import { debug } from "../../../../utils/debugLogger";

/** True when the URL is safe to send to social APIs (public http(s)). */
export function isPublicHttpUrl(url: string): boolean {
  const t = url.trim();
  if (!t) return false;
  try {
    const u = new URL(t);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function dataUrlToFile(dataUrl: string): File {
  const comma = dataUrl.indexOf(",");
  if (comma === -1) throw new Error("Invalid data URL");
  const header = dataUrl.slice(0, comma);
  const base64 = dataUrl.slice(comma + 1);
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch?.[1] || "application/octet-stream";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const ext =
    mime === "image/jpeg"
      ? "jpg"
      : mime === "image/png"
        ? "png"
        : mime === "image/webp"
          ? "webp"
          : mime === "image/gif"
            ? "gif"
            : "img";
  return new File([bytes], `canvas-upload-${Date.now()}.${ext}`, { type: mime });
}

async function blobUrlToFile(blobUrl: string): Promise<File> {
  const res = await fetch(blobUrl);
  const blob = await res.blob();
  const ext = blob.type.split("/")[1]?.split("+")[0] || "bin";
  return new File([blob], `canvas-upload-${Date.now()}.${ext}`, { type: blob.type });
}

/**
 * Canvas stores local picks as data: or blob: URLs. Social publish strips non-public URLs
 * (`normalizePublicMediaUrl`). Upload images to S3 and replace with CloudFront URLs before save/publish.
 */
export async function resolveDraftLocalImageUrl(
  draft: DraftPost,
  customerId: string,
  userEmail: string,
  token: string
): Promise<DraftPost> {
  const uploadLocalUrl = async (raw: string): Promise<string> => {
    const urlStr = raw.trim();
    if (!urlStr || isPublicHttpUrl(urlStr)) return urlStr;
    if (!urlStr.startsWith("blob:") && !urlStr.startsWith("data:")) return urlStr;

    const file = urlStr.startsWith("blob:") ? await blobUrlToFile(urlStr) : dataUrlToFile(urlStr);
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      return urlStr;
    }

    const result = await uploadMedia({
      file,
      customerId,
      userEmail,
      token,
      mediaType: "processed",
      additionalMetadata: { source: "social_canvas" },
      tags: ["social_canvas", `platform:${draft.platform}`],
    });

    if (!result.success || !result.cloudFrontUrl) {
      throw new Error(
        result.errorMessage ||
          "Could not upload media for publishing. Try again or pick from My Library."
      );
    }
    return result.cloudFrontUrl;
  };

  try {
    const nextDraft = { ...draft };
    if (nextDraft.image_url) {
      nextDraft.image_url = await uploadLocalUrl(nextDraft.image_url);
    }
    if (nextDraft.video_url) {
      nextDraft.video_url = await uploadLocalUrl(nextDraft.video_url);
    }
    if (nextDraft.carousel_slides && nextDraft.carousel_slides.length > 0) {
      nextDraft.carousel_slides = await Promise.all(
        nextDraft.carousel_slides.map(async (slide) => ({
          ...slide,
          url: await uploadLocalUrl(slide.url),
        }))
      );
      if (nextDraft.carousel_slides[0]?.url) {
        nextDraft.image_url = nextDraft.carousel_slides[0].url;
      }
    }
    return nextDraft;
  } catch (err) {
    debug.error("resolveDraftLocalImageUrl:", err);
    throw err instanceof Error
      ? err
      : new Error("Could not prepare media for publishing.");
  }
}

export async function resolveDraftsLocalImageUrls(
  drafts: DraftPost[],
  customerId: string,
  userEmail: string,
  token: string
): Promise<DraftPost[]> {
  return Promise.all(
    drafts.map((d) => resolveDraftLocalImageUrl(d, customerId, userEmail, token))
  );
}
